import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { DocumentTextIcon, MusicalNoteIcon, ShieldExclamationIcon, VideoCameraIcon, XMarkIcon } from "@heroicons/react/24/outline";
import cn from "classnames";
import Button from "../common/Button";
import { api } from "./api";

type Source = "case_audio" | "case_report" | "axon";

type PreviousUpload = {
  id: string;
  source: string;
  caseNumber: string | null;
  parentLabel: string | null;
  fileName: string | null;
  fileSize: string | null;
  createdAt: string | null;
  metadata?: { audioType?: string; duration?: number };
};

type CaseReport = { id: string; caseNumber: string | null; title: string; status: string; updatedAt: string | null };

type AxonItem = { id: string; title: string; recordedOn: string; duration: number; fileType: string; size: number; owner: string | null };

type Selected = { source: Source; id: string; title?: string; label: string };

const TABS: { key: Source; label: string }[] = [
  { key: "case_audio", label: "Case uploads" },
  { key: "case_report", label: "My reports" },
  { key: "axon", label: "Bodycam (Axon)" },
];

/**
 * Import material the officer already has in the platform into an
 * investigation: audio uploaded through the case flow, their own written
 * reports (rendered to PDF and read as official case documents), and Axon
 * bodycam evidence. Everything is copied server-side — no re-upload.
 */
const ImportModal: React.FC<{
  open: boolean;
  onClose: () => void;
  investigationId: string;
  attested: boolean;
  onAttest: () => Promise<void>;
  onImported: () => void;
}> = ({ open, onClose, investigationId, attested, onAttest, onImported }) => {
  const [tab, setTab] = useState<Source>("case_audio");
  const [uploads, setUploads] = useState<PreviousUpload[] | null>(null);
  const [reports, setReports] = useState<CaseReport[] | null>(null);
  const [axonItems, setAxonItems] = useState<AxonItem[] | null>(null);
  const [hasAxon, setHasAxon] = useState(false);
  const [selected, setSelected] = useState<Map<string, Selected>>(new Map());
  const [importing, setImporting] = useState(false);
  const [attestChecked, setAttestChecked] = useState(false);
  const [attesting, setAttesting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected(new Map());
    axios
      .get<{ files: PreviousUpload[] }>("/api/cases/previous-uploads", { params: { limit: 100 } })
      .then((r) => setUploads(r.data.files))
      .catch(() => setUploads([]));
    api
      .importCaseReports()
      .then(setReports)
      .catch(() => setReports([]));
    axios
      .get<{ integration: { enabled: boolean } | null }>("/api/settings/evidence-integration")
      .then((r) => {
        const connected = Boolean(r.data.integration?.enabled);
        setHasAxon(connected);
        if (connected) {
          axios
            .get<{ evidence: AxonItem[] }>("/api/evidence", { params: { limit: 50 } })
            .then((x) => setAxonItems((x.data.evidence || []).filter((e) => e.fileType === "video")))
            .catch(() => setAxonItems([]));
        } else {
          setAxonItems([]);
        }
      })
      .catch(() => {
        setHasAxon(false);
        setAxonItems([]);
      });
  }, [open]);

  const toggle = (item: Selected) => {
    setSelected((prev) => {
      const next = new Map(prev);
      const key = `${item.source}:${item.id}`;
      if (next.has(key)) next.delete(key);
      else next.set(key, item);
      return next;
    });
  };

  const doImport = async () => {
    const items = [...selected.values()].map((s) => ({ source: s.source, id: s.id, title: s.title }));
    if (!items.length) return;
    setImporting(true);
    try {
      const results = await api.importFiles(investigationId, items);
      const ok = results.filter((r) => r.fileId);
      const failed = results.filter((r) => r.error);
      if (ok.length) toast.success(`Importing ${ok.length} item${ok.length === 1 ? "" : "s"} — analysis runs on the server.`);
      for (const f of failed.slice(0, 3)) {
        const label = selected.get(`${f.source}:${f.id}`)?.label || f.id;
        toast.error(`${label}: ${f.error}`);
      }
      if (ok.length) {
        onImported();
        onClose();
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.error === "PRIVILEGE_ATTESTATION_REQUIRED" ? "Privilege attestation required" : "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const confirmAttestation = async () => {
    setAttesting(true);
    try {
      await onAttest();
    } catch {
      toast.error("Could not record attestation");
    } finally {
      setAttesting(false);
    }
  };

  const visibleTabs = useMemo(() => TABS.filter((t) => t.key !== "axon" || hasAxon), [hasAxon]);

  if (!open) return null;

  const rows = (children: React.ReactNode, loading: boolean, empty: string) => (
    <div className="min-h-[16rem] max-h-[50vh] overflow-y-auto rounded-xl border border-base-300 divide-y divide-base-300">
      {loading ? (
        <div className="p-6 text-center text-base-content/50">
          <span className="loading loading-spinner loading-sm" />
        </div>
      ) : (
        children || <div className="p-6 text-center text-sm text-base-content/60">{empty}</div>
      )}
    </div>
  );

  const row = (item: Selected, icon: React.ReactNode, primary: string, secondary: string) => {
    const key = `${item.source}:${item.id}`;
    const checked = selected.has(key);
    return (
      <label key={key} className={cn("flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors", checked ? "bg-blue-50 dark:bg-blue-900/40" : "hover:bg-base-200/60")}>
        <input type="checkbox" className="checkbox checkbox-sm [--chkbg:theme(colors.blue.500)] [--chkfg:white] checked:border-blue-500" checked={checked} onChange={() => toggle(item)} />
        <span className="size-8 shrink-0 rounded-lg bg-base-200 text-base-content/60 flex items-center justify-center">{icon}</span>
        <span className="min-w-0">
          <span className="block text-sm text-base-content truncate">{primary}</span>
          <span className="block text-[11px] text-base-content/50 truncate">{secondary}</span>
        </span>
      </label>
    );
  };

  return (
    <dialog open className="modal modal-open">
      <div className="modal-box max-w-2xl p-0 overflow-hidden rounded-2xl">
        <div className="flex items-start justify-between gap-3 px-6 pt-5 pb-3">
          <div>
            <h3 className="text-lg font-bold text-base-content leading-tight">Import from your cases</h3>
            <p className="text-sm text-base-content/60 mt-0.5">Copies happen on the server — nothing re-uploads from this device.</p>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded-md text-base-content/50 hover:bg-base-200 hover:text-base-content" title="Close">
            <XMarkIcon className="size-5" />
          </button>
        </div>

        {!attested ? (
          <div className="px-6 pb-6">
            <div className="flex items-start gap-3 rounded-xl bg-amber-50 dark:bg-amber-950/40 p-4">
              <ShieldExclamationIcon className="size-6 shrink-0 text-amber-600 dark:text-amber-400" />
              <p className="text-sm text-base-content/80">
                Before anything is added to this investigation, certify that the material is not attorney-client privileged.
              </p>
            </div>
            <label className={cn("mt-3 flex items-start gap-3 rounded-xl border px-4 py-3 text-sm cursor-pointer transition-colors", attestChecked ? "border-blue-300 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/40" : "border-base-300 bg-base-200/50 hover:bg-base-200")}>
              <input type="checkbox" className="checkbox checkbox-sm mt-0.5 [--chkbg:theme(colors.blue.500)] [--chkfg:white] checked:border-blue-500" checked={attestChecked} onChange={(e) => setAttestChecked(e.target.checked)} />
              <span className="text-base-content/90 leading-6">
                I certify that the recordings I add to this investigation are not attorney-client privileged communications, and I will mark any file privileged if I later learn otherwise.
              </span>
            </label>
            <div className="mt-4 flex justify-end">
              <Button variant="primary" size="sm" disabled={!attestChecked || attesting} onClick={confirmAttestation}>
                Continue
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="px-6 flex items-center gap-1">
              {visibleTabs.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  className={cn(
                    "px-3 py-1.5 rounded-lg text-sm font-medium transition-colors",
                    tab === t.key ? "bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300" : "text-base-content/60 hover:bg-base-200 hover:text-base-content"
                  )}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div className="px-6 pt-3">
              {tab === "case_audio" &&
                rows(
                  uploads?.length
                    ? uploads.map((u) =>
                        row(
                          { source: "case_audio", id: u.id, label: u.fileName || "audio" },
                          <MusicalNoteIcon className="size-4" />,
                          u.fileName || "Audio file",
                          [u.parentLabel || (u.caseNumber ? `Case ${u.caseNumber}` : null), u.fileSize, u.createdAt ? new Date(u.createdAt).toLocaleDateString() : null]
                            .filter(Boolean)
                            .join(" · ")
                        )
                      )
                    : null,
                  uploads === null,
                  "No completed audio uploads found on your cases."
                )}
              {tab === "case_report" &&
                rows(
                  reports?.length
                    ? reports.map((r) =>
                        row(
                          { source: "case_report", id: r.id, label: r.title },
                          <DocumentTextIcon className="size-4" />,
                          r.title,
                          [r.caseNumber ? `Case ${r.caseNumber}` : null, r.updatedAt ? new Date(r.updatedAt).toLocaleDateString() : null].filter(Boolean).join(" · ")
                        )
                      )
                    : null,
                  reports === null,
                  "No written reports found on your cases."
                )}
              {tab === "axon" &&
                rows(
                  axonItems?.length
                    ? axonItems.map((e) =>
                        row(
                          { source: "axon", id: e.id, title: e.title, label: e.title },
                          <VideoCameraIcon className="size-4" />,
                          e.title || "Bodycam video",
                          [e.recordedOn ? new Date(e.recordedOn).toLocaleDateString() : null, e.owner].filter(Boolean).join(" · ")
                        )
                      )
                    : null,
                  axonItems === null,
                  "No video evidence found in Axon."
                )}
              {tab === "case_report" && (
                <p className="mt-2 text-[11px] text-base-content/50">
                  Your report is rendered to PDF and read as an official case document: authoritative for names and dates, flagged only for discrepancies and new information.
                </p>
              )}
            </div>

            <div className="px-6 py-4 flex items-center gap-2">
              <span className="text-sm text-base-content/60">{selected.size} selected</span>
              <div className="ml-auto flex items-center gap-2">
                <Button variant="secondary" size="sm" onClick={onClose}>
                  Cancel
                </Button>
                <Button variant="primary" size="sm" disabled={!selected.size || importing} onClick={doImport}>
                  {importing ? "Importing…" : `Import${selected.size ? ` ${selected.size}` : ""}`}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
      <button type="button" className="modal-backdrop" onClick={onClose} aria-label="Close" />
    </dialog>
  );
};

export default ImportModal;
